import { useMemo, useState } from 'react';
import { Bot } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { CHAT_MESSAGE_ACTION_ICON_CLASS } from 'constant/chat-layout';
import { cn } from 'lib/utils';
import type { MessageGeneration } from '../types/chat';
import { groupMessageSubagentRunsBySubagentId } from '../utils/parse-message-subagent-runs';

interface MessageSubagentRunsProps {
  message: MessageGeneration;
}

const formatSubagentRunDuration = (durationMs?: number): string => {
  if (durationMs == null) {
    return '—';
  }
  if (durationMs >= 1000) {
    return `${(durationMs / 1000).toFixed(2)}s`;
  }
  return `${durationMs}ms`;
};

const formatGroupedSubagentLabel = (subagentName: string, count: number): string =>
  count > 1 ? `${subagentName}(${count}x)` : subagentName;

export function MessageSubagentRuns({ message }: MessageSubagentRunsProps) {
  const { t } = useTranslation();
  const subagentRuns = message.subagentRuns;
  const [isOpen, setIsOpen] = useState(false);

  const groupedSubagentRuns = useMemo(
    () => (subagentRuns ? groupMessageSubagentRunsBySubagentId(subagentRuns) : []),
    [subagentRuns]
  );

  const totalDurationMs = useMemo(
    () => groupedSubagentRuns.reduce((sum, run) => sum + (run.totalDurationMs ?? 0), 0),
    [groupedSubagentRuns]
  );

  const hasAnyDuration = groupedSubagentRuns.some((run) => run.totalDurationMs != null);
  const showTotalRow = groupedSubagentRuns.length > 1 && hasAnyDuration;
  const runCount = subagentRuns?.length ?? 0;

  if (!subagentRuns?.length) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={isOpen} onOpenChange={setIsOpen}>
        <TooltipTrigger asChild>
          <div className="relative inline-block">
            <button
              type="button"
              className="relative inline-flex shrink-0 size-8 items-center justify-center rounded-full p-0 hover:bg-muted"
              onClick={(e) => e.stopPropagation()}
              title={t('SUBAGENTS') || 'Subagents'}
              aria-label={t('MESSAGE_SUBAGENT_RUNS') || 'Subagent runs'}
            >
              <Bot className={cn(CHAT_MESSAGE_ACTION_ICON_CLASS, 'size-5')} strokeWidth={1.5} />
            </button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="w-[min(100vw-2rem,288px)] p-0">
          <div className="flex flex-col overflow-hidden rounded-md">
            <div className="flex items-center justify-between gap-3 border-b border-primary-foreground/20 px-3 py-2">
              <span className="text-xs font-semibold">
                {t('MESSAGE_SUBAGENT_RUNS') || 'Subagent runs'}
              </span>
              <span className="text-[10px] opacity-70">
                {runCount === 1
                  ? t('SUBAGENT_RUN_COUNT_ONE') || '1 run'
                  : t('SUBAGENT_RUN_COUNT_MANY', { count: runCount }) || `${runCount} runs`}
              </span>
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 border-b border-primary-foreground/10 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide opacity-60">
              <span>{t('SUBAGENT') || 'Subagent'}</span>
              <span className="text-right">{t('SUBAGENT_RUN_DURATION') || 'Duration'}</span>
            </div>

            <div className="max-h-[min(50vh,220px)] overflow-y-auto overscroll-contain">
              {groupedSubagentRuns.map((run, index) => (
                <div
                  key={run.runId}
                  className={cn(
                    'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 px-3 py-2 text-xs',
                    index % 2 === 1 && 'bg-primary-foreground/5',
                    index < groupedSubagentRuns.length - 1 && 'border-b border-primary-foreground/10'
                  )}
                >
                  <span
                    className="truncate font-medium"
                    title={formatGroupedSubagentLabel(run.subagentName, run.count)}
                  >
                    {run.subagentName}
                    {run.count > 1 && (
                      <span className="font-normal opacity-80">({run.count}x)</span>
                    )}
                  </span>
                  <span className="shrink-0 text-right font-medium tabular-nums">
                    {formatSubagentRunDuration(run.totalDurationMs)}
                  </span>
                </div>
              ))}
            </div>

            {showTotalRow && (
              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 border-t border-primary-foreground/20 bg-primary-foreground/10 px-3 py-2 text-xs">
                <span className="font-medium opacity-90">{t('TOTAL') || 'Total'}</span>
                <span className="shrink-0 text-right font-semibold tabular-nums">
                  {formatSubagentRunDuration(totalDurationMs)}
                </span>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
